import {
  Radar,
  Anchor,
  Crosshair,
  Activity,
  Truck,
  ClipboardList,
  HardHat,
  LineChart,
  Wrench,
  Leaf,
  Users,
  Gauge,
  type LucideIcon,
} from 'lucide-react';

export type AppStatus = 'live' | 'preview';

export interface PortalApp {
  /** Stable id. */
  id: string;
  name: string;
  /** One-line description shown on the spotlight + the tile tooltip/app header. */
  blurb: string;
  /** lucide-react icon component. */
  Icon: LucideIcon;
  /**
   * Brand tint for the app's icon tile — a distinct, professional hue per app so the
   * launcher reads as an app suite (Microsoft 365 / Google Workspace style). Rendered as a
   * white glyph on a tinted rounded square, so it stays legible on every theme background.
   */
  tint: string;
  /** 'live' = real app (HMH Watch); 'preview' = dummy framework placeholder. */
  status: AppStatus;
  /** In-portal path, e.g. /wwd. Each app lives at hmhintel.com<to>. */
  to: string;
  /** When true, the tile is shown only to admins. The data API is admin-gated server-side too. */
  adminOnly?: boolean;
  /**
   * Interim only: if set, the tile + switcher open this URL in a new tab instead of routing
   * in-portal. Unused now that HMH Watch is in-shell (BFF proxy at /hmhwatch); kept for future apps.
   */
  externalUrl?: string;
}

// The app registry — the single source of truth for the launcher tiles and the app switcher.
// Each app lives at a clean path under the portal (hmhintel.com<to>). HMH Watch and World Wide
// Drilling are real apps; the rest are dummy placeholders with relevant HMH domain names.
export const apps: PortalApp[] = [
  {
    id: 'hmh-watch',
    name: 'HMH Watch',
    blurb: 'Market & competitor intelligence',
    Icon: Radar,
    tint: '#334b9b', // HMH corporate blue
    status: 'live',
    to: '/hmhwatch',
  },
  {
    id: 'world-wide-drilling',
    name: 'World Wide Drilling',
    blurb: 'Offshore rig fleet & status',
    Icon: Anchor,
    tint: '#0e7490', // ocean cyan
    status: 'live',
    to: '/wwd',
  },
  {
    id: 'foundry-usage',
    name: 'Foundry Usage',
    blurb: 'Azure AI Foundry token, call & cost usage across all apps',
    Icon: Gauge,
    tint: '#a16207', // amber — metering / observability
    status: 'live',
    to: '/foundry-usage',
    adminOnly: true,
  },
  {
    // Real module (spa/src/components/mna) on typed fixtures; 'preview' until the A6
    // graduation flips it 'live' (the WWD pre-graduation staging pattern).
    id: 'mna',
    name: 'M&A Radar',
    blurb: 'Global acquisition targets & distress radar',
    Icon: Crosshair,
    tint: '#4f46e5', // indigo
    status: 'live',
    to: '/mna',
  },
  {
    id: 'fleet-pulse',
    name: 'Fleet Pulse',
    blurb: 'Live rig & vessel telemetry',
    Icon: Activity,
    tint: '#0d9488', // teal
    status: 'preview',
    to: '/fleet',
  },
  {
    id: 'supply-chain-radar',
    name: 'Supply Chain Radar',
    blurb: 'Parts, inventory & lead times',
    Icon: Truck,
    tint: '#b45309', // bronze
    status: 'preview',
    to: '/supply',
  },
  {
    id: 'tender-hub',
    name: 'Tender Hub',
    blurb: 'Bids, RFQs & contract pipeline',
    Icon: ClipboardList,
    tint: '#7c3aed', // violet
    status: 'preview',
    to: '/tenders',
  },
  {
    id: 'hse-monitor',
    name: 'HSE Monitor',
    blurb: 'Safety, incidents & compliance',
    Icon: HardHat,
    tint: '#e11d48', // safety rose
    status: 'preview',
    to: '/hse',
  },
  {
    id: 'energy-markets',
    name: 'Energy Markets',
    blurb: 'Oil, gas & commodity prices',
    Icon: LineChart,
    tint: '#0284c7', // sky
    status: 'preview',
    to: '/markets',
  },
  {
    id: 'aftermarket-service',
    name: 'Aftermarket Service',
    blurb: 'Service contracts, spares & RMA',
    Icon: Wrench,
    tint: '#475569', // slate
    status: 'preview',
    to: '/service',
  },
  {
    id: 'carbon-ledger',
    name: 'Carbon Ledger',
    blurb: 'Emissions & ESG reporting',
    Icon: Leaf,
    tint: '#15803d', // green
    status: 'preview',
    to: '/carbon',
  },
  {
    id: 'talent-grid',
    name: 'Talent Grid',
    blurb: 'Workforce, crew rotation & certs',
    Icon: Users,
    tint: '#c026d3', // fuchsia
    status: 'preview',
    to: '/talent',
  },
];

/** Look an app up by its path slug (the segment after '/', e.g. 'wwd'). */
export function getAppBySlug(slug: string | undefined): PortalApp | undefined {
  if (!slug) return undefined;
  return apps.find((a) => a.to === `/${slug}`);
}
